import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

function vibrate() {
  try { navigator.vibrate?.([200, 100, 200]); } catch (_) {}
}

function playDoorbell() {
  vibrate();
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.4, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };
    // Resume primero (Chrome bloquea AudioContext sin gesto del usuario)
    ctx.resume().then(() => {
      // Ding (E5) … Dong (B4)
      playTone(659, ctx.currentTime, 0.6);
      playTone(494, ctx.currentTime + 0.65, 0.8);
    });
  } catch (_) {}
}

export default function CompanyOrderNotifier() {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const bellIntervalRef = useRef(null);
  const socketRef = useRef(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();

  const companyId = user?.company?.id;
  const isCompany = user?.role === "company" && companyId;

  // ── Consultar pedidos pendientes al montar ───────────────────
  useEffect(() => {
    if (!isCompany) return;

    const fetchPending = async () => {
      try {
        const res = await API.get(`/orders/company/${companyId}`, {
          params: { status: "pendiente" },
        });
        const list = res.data?.data?.data ?? res.data?.data ?? [];
        setPendingCount(list.length);
      } catch (_) {}
    };

    fetchPending();
  }, [isCompany, companyId]);

  // ── Timbre periódico mientras haya pedidos pendiente ─────────
  useEffect(() => {
    if (!isCompany) return;

    if (pendingCount > 0) {
      if (!bellIntervalRef.current) {
        playDoorbell();
        bellIntervalRef.current = setInterval(playDoorbell, 30000);
      }
    } else {
      clearInterval(bellIntervalRef.current);
      bellIntervalRef.current = null;
    }

    return () => {};
  }, [pendingCount, isCompany]);

  // ── Socket.io global ─────────────────────────────────────────
  useEffect(() => {
    if (!isCompany) return;

    const token =
      localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;

    const socket = io(SOCKET_URL, {
      transports: ["websocket"],
      auth: { token },
    });
    socketRef.current = socket;

    const refreshCount = async () => {
      try {
        const res = await API.get(`/orders/company/${companyId}`, {
          params: { status: "pendiente" },
        });
        const list = res.data?.data?.data ?? res.data?.data ?? [];
        setPendingCount(list.length);
      } catch (_) {}
    };

    socket.on("order:new", (newOrder) => {
      if (newOrder.companyId !== companyId && newOrder.company?.id !== companyId)
        return;
      refreshCount();
      playDoorbell();
    });

    // Re-consulta el conteo real en lugar de decrementar (evita desincronía)
    socket.on("order:status", refreshCount);

    socket.on("chat:message", ({ senderRole, message }) => {
      if (senderRole === "user") {
        toast.info(`💬 El cliente te escribió: ${message}`, { autoClose: 7000 });
      } else if (senderRole === "ryder") {
        toast.info(`💬 El domiciliario te escribió: ${message}`, { autoClose: 7000 });
      }
    });

    // Polling cada 30 s como respaldo si el socket falla
    const pollingInterval = setInterval(refreshCount, 30000);

    return () => {
      socket.disconnect();
      clearInterval(bellIntervalRef.current);
      bellIntervalRef.current = null;
      clearInterval(pollingInterval);
    };
    // eslint-disable-next-line
  }, [isCompany, companyId]);

  if (!isCompany || pendingCount === 0) return null;

  return (
    <button
      onClick={() => navigate(`/company/${companyId}/orders`)}
      className="fixed bottom-20 right-4 z-50 flex items-center gap-2
        bg-amber-500 hover:bg-amber-600 text-white font-bold
        px-4 py-3 rounded-full shadow-lg
        animate-bounce transition"
      title="Ver pedidos pendientes"
    >
      🛎️
      <span className="text-sm">
        {pendingCount} pedido{pendingCount > 1 ? "s" : ""} pendiente{pendingCount > 1 ? "s" : ""}
      </span>
      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping" />
    </button>
  );
}
